import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, FolderSearch } from 'lucide-react';
import { mockData } from '../../data/mock';

const getPerView = () => (typeof window !== 'undefined' && window.matchMedia('(max-width:900px)').matches ? 1 : 3);

export const CasesBand = () => {
  const [perView, setPerView] = useState(getPerView);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('All');
  const trackRef = useRef(null);
  const studies = mockData.caseStudies.slice(0, 9);
  const industries = ['All', ...Array.from(new Set(studies.map((s) => s.industry)))];
  const visible = filter === 'All' ? studies : studies.filter((s) => s.industry === filter);
  const pages = Math.ceil(visible.length / perView);

  useEffect(() => {
    const onResize = () => setPerView(getPerView());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(0, Math.ceil(visible.length / perView) - 1)));
  }, [perView, visible.length]);

  useEffect(() => {
    setPage(0);
    trackRef.current?.scrollTo({ left: 0 });
  }, [filter]);

  const goToPage = useCallback((p) => {
    const clamped = Math.max(0, Math.min(p, pages - 1));
    const child = trackRef.current?.children[clamped * perView];
    if (child) trackRef.current.scrollTo({ left: child.offsetLeft, behavior: 'smooth' });
    setPage(clamped);
  }, [pages, perView]);

  const onScroll = useCallback(() => {
    const t = trackRef.current;
    if (!t) return;
    let nearest = 0;
    let min = Infinity;
    Array.from(t.children).forEach((c, i) => {
      const d = Math.abs(c.offsetLeft - t.scrollLeft);
      if (d < min) { min = d; nearest = i; }
    });
    setPage(Math.floor(nearest / perView));
  }, [perView]);

  return (
    <section className="section cases" id="cases">
      <div className="container">
        <div className="heading">
          <p className="eyebrow eyebrow-light">Success stories</p>
          <h2>Dashboards we&rsquo;ve built</h2>
          <p>Sample builds across healthcare, finance, retail and more &mdash; click any to explore it live.</p>
        </div>

        {industries.length > 2 && (
          <div className="case-filters" data-testid="case-filters">
            {industries.map((ind) => (
              <button
                key={ind}
                className={`case-filter ${filter === ind ? 'active' : ''}`}
                onClick={() => setFilter(ind)}
                data-testid={`case-filter-${ind.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {ind}
              </button>
            ))}
          </div>
        )}

        {visible.length > 0 && (
          <p className="case-count" data-testid="case-count">
            Showing {visible.length} of {studies.length} {studies.length === 1 ? 'story' : 'stories'}
            {filter !== 'All' && <> in <strong>{filter}</strong></>}
          </p>
        )}

        {visible.length === 0 ? (
          <div className="case-empty" data-testid="cases-empty">
            <FolderSearch className="case-empty-icon" />
            <p className="case-empty-title">No stories yet in {filter}</p>
            <p className="case-empty-sub">We&rsquo;re adding more soon. Check back shortly, or explore everything we&rsquo;ve built.</p>
            <button className="case-filter active" onClick={() => setFilter('All')} data-testid="case-empty-reset">View all stories</button>
          </div>
        ) : (
        <div className="case-slider">
          <div className="case-track" ref={trackRef} onScroll={onScroll} data-testid="cases-track">
            {visible.map((s, i) => (
              <article className="case" key={s.title} data-testid={`case-${i}`}>
                <div className="case-visual">
                  {s.image && <img src={s.image} alt={s.title} loading="lazy" />}
                  <span className="case-tag">{s.industry.toUpperCase()}</span>
                  {s.sampleBuild && <span className="case-badge" data-testid={`case-sample-badge-${i}`}>Sample build</span>}
                </div>
                <div className="case-body">
                  <h3><a href={`/case-studies/${s.slug}`} data-testid={`case-title-link-${i}`}>{s.title}</a></h3>
                  <p>{s.description}</p>
                  {s.results?.[0] && <span className="result">{s.results[0]}</span>}
                  <div>
                    <a className="case-button" href={`/case-studies/${s.slug}`} data-testid={`case-detail-link-${i}`}>
                      Read the full story &rarr;
                    </a>
                    {s.link && (
                      <a className="case-button" href={s.link} target="_blank" rel="noreferrer" data-testid={`case-link-${i}`}>
                        View Live Dashboard &rarr;
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {pages > 1 && (
            <div className="case-nav" data-testid="cases-nav">
              <button className="case-arrow" onClick={() => goToPage(page - 1)} disabled={page === 0} data-testid="case-prev" aria-label="Previous case studies">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="case-dots">
                {Array.from({ length: pages }).map((_, p) => (
                  <button key={p} className={`case-dot ${p === page ? 'active' : ''}`} onClick={() => goToPage(p)} data-testid={`case-dot-${p}`} aria-label={`Go to case studies page ${p + 1}`} />
                ))}
              </div>
              <button className="case-arrow" onClick={() => goToPage(page + 1)} disabled={page >= pages - 1} data-testid="case-next" aria-label="Next case studies">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
        )}
        <div style={{ textAlign: 'center', marginTop: '2.5rem', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a className="button" href="/dashboards" data-testid="dashboards-teaser-btn">View featured dashboards &rarr;</a>
          <a className="case-button" href="/case-studies" data-testid="cases-view-all">View all case studies &rarr;</a>
        </div>
      </div>
    </section>
  );
};
